import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { SkeletonTable } from './Skeleton';
import EmptyState from './EmptyState';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';
import { breakpoints } from '../../design-system/tokens/breakpoints';
const TableContainer = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  max-height: min(70vh, calc(100vh - 280px));
  border-radius: ${borders.radius.lg};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  box-shadow: ${shadows.sm};
`;
const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: ${props => props.theme.colors.surface};
`;
const Thead = styled.thead`
  background: ${props => props.theme.colors.surfaceHover};
`;
const Th = styled.th<{ sortable?: boolean }>`
  padding: ${spacing[3]} ${spacing[4]};
  text-align: left;
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  vertical-align: middle;
  position: sticky;
  top: 0;
  z-index: 3;
  background: ${props => props.theme.colors.surfaceHover};
  border-bottom: ${borders.width.base} solid
    ${props => props.theme.colors.border};
  ${props =>
    props.sortable &&
    ` cursor: pointer; user-select: none; transition: ${animations.transition(['background-color'], animations.duration.fast, animations.easing.easeOut)}; &:hover { background: ${props.theme.colors.border}; } `}
`;
const StickyTh = styled(Th)`
  right: 0;
  z-index: 4;
  box-shadow: -4px 0 8px -2px ${props => props.theme.colors.border};
`;
const Tbody = styled.tbody``;
const Tr = styled.tr`
  border-bottom: ${borders.width.thin} solid
    ${props => props.theme.colors.border};
  transition: ${animations.transition(
    ['background-color'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  &:hover {
    background: ${props => props.theme.colors.surfaceHover};
  }
  &:last-child {
    border-bottom: none;
  }
`;
const Td = styled.td<{ $wrap?: boolean }>`
  padding: ${spacing[3]} ${spacing[4]};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.text};
  white-space: ${props => (props.$wrap ? 'normal' : 'nowrap')};
  vertical-align: middle;
`;
const Pagination = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[4]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};

  ${breakpoints.down.sm} {
    flex-direction: column;
    align-items: stretch;
  }
`;
const PageInfo = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;
const PageButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};

  ${breakpoints.down.sm} {
    justify-content: center;
  }
`;
const PageButton = styled.button<{ active?: boolean }>`
  padding: ${spacing[2]} ${spacing[3]};
  min-height: 44px;
  min-width: 44px;
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  background: ${props =>
    props.active ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${props =>
    props.active ? props.theme.colors.surface : props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  font-weight: ${props =>
    props.active
      ? typography.fontWeight.semibold
      : typography.fontWeight.medium};
  cursor: pointer;
  transition: ${animations.transition(
    ['background-color', 'transform'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  &:hover:not(:disabled) {
    background: ${props =>
      props.active
        ? props.theme.colors.primaryDark
        : props.theme.colors.surfaceHover};
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;
export interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  /** When set, this column is rendered as row actions; cell clicks do not trigger onRowClick. */
  actions?: (item: T) => React.ReactNode;
  sortable?: boolean;
  /** Allow this column's cells to wrap instead of forcing a single line. */
  wrap?: boolean;
}
interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  emptyMessage?: string;
  /** When set, clicking a row (except the actions cell) calls this. */
  onRowClick?: (item: T) => void;
}
const TrClickable = styled(Tr)<{ clickable?: boolean }>`
  ${props => props.clickable && 'cursor: pointer;'}
`;
const ActionsCell = styled(Td)`
  white-space: nowrap;
  position: sticky;
  right: 0;
  z-index: 2;
  background: ${props => props.theme.colors.surface};
  box-shadow: -4px 0 8px -2px ${props => props.theme.colors.border};
  tr:hover & {
    background: ${props => props.theme.colors.surfaceHover};
  }
`;

export function DataTable<T extends { id: string }>({
  data,
  columns,
  loading = false,
  page = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
  emptyMessage,
  onRowClick,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const resolvedEmptyMessage: string =
    emptyMessage ?? ((t('common.noData') as string) || 'No data available');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const columnCount = columns.length;
  if (loading) {
    return (
      <TableContainer>
        <SkeletonTable rows={pageSize} columns={columnCount} />
      </TableContainer>
    );
  }
  if (data.length === 0) {
    return (
      <TableContainer role="region" aria-label={t('common.table', { defaultValue: 'Table' })}>
        <div role="status" aria-live="polite">
          <EmptyState title={resolvedEmptyMessage} />
        </div>
      </TableContainer>
    );
  }
  return (
    <TableContainer role="region" aria-label={t('common.table', { defaultValue: 'Table' })}>
      <Table>
        <Thead>
          <tr>
            {columns.map(column =>
              column.actions ? (
                <StickyTh key={column.key} scope="col">
                  {column.header}
                </StickyTh>
              ) : (
                <Th key={column.key} sortable={column.sortable} scope="col">
                  {column.header}
                </Th>
              )
            )}
          </tr>
        </Thead>
        <Tbody>
          {data.map(item => (
            <TrClickable
              key={item.id}
              clickable={!!onRowClick}
              onClick={() => onRowClick?.(item)}
            >
              {columns.map(column => (
                column.actions ? (
                  <ActionsCell
                    key={column.key}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {column.actions(item)}
                  </ActionsCell>
                ) : (
                  <Td key={column.key} $wrap={column.wrap}>
                    {column.render
                      ? column.render(item)
                      : (item as Record<string, unknown>)[column.key] as React.ReactNode}
                  </Td>
                )
              ))}
            </TrClickable>
          ))}
        </Tbody>
      </Table>
      {onPageChange && totalPages > 1 && (
        <Pagination>
          {' '}
          <PageInfo>
            {' '}
            {t('common.showingResults', {
              start: (page - 1) * pageSize + 1,
              end: Math.min(page * pageSize, total),
              total,
            })}
          </PageInfo>{' '}
          <PageButtons>
            {' '}
            <PageButton
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
            >
              {t('common.previous')}
            </PageButton>{' '}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PageButton
                  key={pageNum}
                  active={pageNum === page}
                  onClick={() => onPageChange(pageNum)}
                >
                  {' '}
                  {pageNum}{' '}
                </PageButton>
              );
            })}{' '}
            <PageButton
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
            >
              {t('common.next')}
            </PageButton>{' '}
          </PageButtons>{' '}
        </Pagination>
      )}{' '}
    </TableContainer>
  );
}
export default DataTable;
